import { Button, Card, CardContent, Grid } from "@mui/material";
import SettingsReport from "./SettingsReport";
import {
  PerformanceReportTypeEnum,
  ReportValueCardProps,
  PDFReportRequest,
  PDFReportClientInfoItem,
  PDFReportThemeInfoItem,
  ReportChartDataPoint,
  ReportPdfResponse,
  OrionTransaction,
  BucketPairReportStatus,
  BucketReportStatusEnum,
  ReportThemedCardConfig,
  PDFReportValueType,
  PDFReportChartType,
} from "helpers/types";
import ChartsReport from "./Charts/ChartsReport";
import { SingleValueCardColor, SingleValueCardType } from "constants/index";
import {
  StrategyReportSingleValueCardLabels,
  StrategyReportSingleValueCardDescriptions,
  StrategyReportThemedCardLabels,
  StrategyReportThemedCardDescriptions,
  StrategyReportThemedCardValuesLabels,
  StrategyReportThemedCardBlurbs,
} from "helpers/constants";
import { toPdfValues } from "./reportPdfUtils";
import SingleValueCard from "./SingleValueCard";
import MultiValuesCard from "./MultiValuesCard";
import CurrentAllocationChartReport from "./Charts/CurrentAllocationChartReport";
import { DrawdownComparisonChart } from "./Charts/DrawdownComparisonChart";
import { useStrategyReportValue } from "hooks/useReportStorage";
import { useMemo, useState, useCallback, useEffect } from "react";
import {
  useGetBucketReportMutation,
  useGetReportPdfMutation,
} from "features/trading/accounts";
import { BucketNumberEnum } from "features/trading/buckets";
import {
  ReportSettingsProvider,
  useReportSettings,
} from "./ReportSettingsContext";
import TransactionModal from "./TransactionModal";
import RequestNotification from "components/ui/RequestNotification";
import { extractReportErrorMessage } from "./extractReportErrorMessage";

const STRATEGY_REPORT_DEFAULTS = {
  STARTING_VALUE: 10000000,
  ENDING_VALUE: 10450000,
  NET_CHANGE: 450000,
  TIME_WEIGHTED_RETURN: 4.5,
  INCOME_RECEIVED: 125000,
  MARGIN_INTEREST: 15000,
  SHARES_START: 100000,
  SHARES_END: 100000,
  VALUE_START: 5000000,
  VALUE_END: 5200000,
  DIVIDENDS: 45000,
  UPSIDE_PARTICIPATION: 95,
  BASIS: 500000,
  TAX_SAVINGS: 1500000,
  MAX_STOCK_DROP: -15,
  OEA_DROP: -8,
  COLLAR_PL: 75000,
  AVAILABLE_MARGIN_BALANCE: 150000,
  OVERALL_DIVERSIFIED_PERFORMANCE: 4.3,
  TAX_LOSS_HARVEST_SLEEVE: 0.3,
  BEGINNING_CONCENTRATION: 85,
  CURRENT_CONCENTRATION: 20,
  HOLDINGS: 12,
  REALIZED_LOSSES: 150000,
  LOSSES_USED: 50000,
  POOL_REMAINING: 100000,
  UNREALIZED_GAINS: 250000,
  SHARES_SOLD: 5000,
  SELLABLE_TODAY: 15000,
  DRAWDOWN_CHART_LIMIT: 20,
  DRAWDOWN_CHART_NEGATIVE_LIMIT: -20,
};

const FULL_ALLOCATION_PCT = 100;

const DRAWDOWN_CHART_TITLE = "Drawdown Comparison";
const ALLOCATION_CHART_TITLE = "Current Allocation";
const CHART_SERIES_STOCK = "Stock";
const CHART_SERIES_OEA = "OEA";
const CHART_SERIES_STRATEGY = "Strategy";

function StrategyReportContent() {
  // SingleValueCard hooks
  const [startingValue, setStartingValue] = useStrategyReportValue(
    "starting_value_strategy",
    STRATEGY_REPORT_DEFAULTS.STARTING_VALUE,
  );
  const [endingValue, setEndingValue] = useStrategyReportValue(
    "ending_value_strategy",
    STRATEGY_REPORT_DEFAULTS.ENDING_VALUE,
  );
  const [netChange, setNetChange] = useStrategyReportValue(
    "net_change_strategy",
    STRATEGY_REPORT_DEFAULTS.NET_CHANGE,
  );
  const [timeWeightedReturn, setTimeWeightedReturn] = useStrategyReportValue(
    "time_weighted_return_strategy",
    STRATEGY_REPORT_DEFAULTS.TIME_WEIGHTED_RETURN,
  );
  const [incomeReceived, setIncomeReceived] = useStrategyReportValue(
    "income_received_strategy",
    STRATEGY_REPORT_DEFAULTS.INCOME_RECEIVED,
  );
  const [marginInterest, setMarginInterest] = useStrategyReportValue(
    "margin_interest_strategy",
    STRATEGY_REPORT_DEFAULTS.MARGIN_INTEREST,
  );

  // MultiValuesCard hooks - Keep
  const [sharesStart, setSharesStart] = useStrategyReportValue(
    "shares_start_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_START,
  );
  const [sharesEnd, setSharesEnd] = useStrategyReportValue(
    "shares_end_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_END,
  );
  const [valueStart, setValueStart] = useStrategyReportValue(
    "value_start_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_START,
  );
  const [valueEnd, setValueEnd] = useStrategyReportValue(
    "value_end_strategy",
    STRATEGY_REPORT_DEFAULTS.VALUE_END,
  );
  const [dividends, setDividends] = useStrategyReportValue(
    "dividends_strategy",
    STRATEGY_REPORT_DEFAULTS.DIVIDENDS,
  );
  const [upsideParticipation, setUpsideParticipation] = useStrategyReportValue(
    "upside_participation_strategy",
    STRATEGY_REPORT_DEFAULTS.UPSIDE_PARTICIPATION,
  );
  const [basis, setBasis] = useStrategyReportValue(
    "basis_strategy",
    STRATEGY_REPORT_DEFAULTS.BASIS,
  );
  const [taxSavings, setTaxSavings] = useStrategyReportValue(
    "tax_savings_strategy",
    STRATEGY_REPORT_DEFAULTS.TAX_SAVINGS,
  );

  // MultiValuesCard hooks - Protect
  const [maxStockDrop, setMaxStockDrop] = useStrategyReportValue(
    "max_stock_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.MAX_STOCK_DROP,
  );
  const [oeaDrop, setOeaDrop] = useStrategyReportValue(
    "oea_drop_strategy",
    STRATEGY_REPORT_DEFAULTS.OEA_DROP,
  );
  const [collarPL, setCollarPL] = useStrategyReportValue(
    "collar_pl_strategy",
    STRATEGY_REPORT_DEFAULTS.COLLAR_PL,
  );
  const [availableMarginBalance, setAvailableMarginBalance] =
    useStrategyReportValue(
      "available_margin_balance_strategy",
      STRATEGY_REPORT_DEFAULTS.AVAILABLE_MARGIN_BALANCE,
    );

  // MultiValuesCard hooks - Diversify
  const [overallDiversifiedPerformance, setOverallDiversifiedPerformance] =
    useStrategyReportValue(
      "overall_diversified_performance_strategy",
      STRATEGY_REPORT_DEFAULTS.OVERALL_DIVERSIFIED_PERFORMANCE,
    );
  const [taxLossHarvestSleeve, setTaxLossHarvestSleeve] =
    useStrategyReportValue(
      "tax_loss_harvest_sleeve_strategy",
      STRATEGY_REPORT_DEFAULTS.TAX_LOSS_HARVEST_SLEEVE,
    );
  const [beginningConcentration, setBeginningConcentration] =
    useStrategyReportValue(
      "beginning_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.BEGINNING_CONCENTRATION,
    );
  const [currentConcentration, setCurrentConcentration] =
    useStrategyReportValue(
      "current_concentration_strategy",
      STRATEGY_REPORT_DEFAULTS.CURRENT_CONCENTRATION,
    );
  const [holdings, setHoldings] = useStrategyReportValue(
    "holdings_strategy",
    STRATEGY_REPORT_DEFAULTS.HOLDINGS,
  );

  // MultiValuesCard hooks - Harvest Tax Benefits
  const [realizedLosses, setRealizedLosses] = useStrategyReportValue(
    "realized_losses_strategy",
    STRATEGY_REPORT_DEFAULTS.REALIZED_LOSSES,
  );
  const [lossesUsed, setLossesUsed] = useStrategyReportValue(
    "losses_used_strategy",
    STRATEGY_REPORT_DEFAULTS.LOSSES_USED,
  );
  const [poolRemaining, setPoolRemaining] = useStrategyReportValue(
    "pool_remaining_strategy",
    STRATEGY_REPORT_DEFAULTS.POOL_REMAINING,
  );
  const [unrealizedGains, setUnrealizedGains] = useStrategyReportValue(
    "unrealized_gains_strategy",
    STRATEGY_REPORT_DEFAULTS.UNREALIZED_GAINS,
  );
  const [sharesSold, setSharesSold] = useStrategyReportValue(
    "shares_sold_strategy",
    STRATEGY_REPORT_DEFAULTS.SHARES_SOLD,
  );
  const [sellableToday, setSellableToday] = useStrategyReportValue(
    "sellable_today_strategy",
    STRATEGY_REPORT_DEFAULTS.SELLABLE_TODAY,
  );

  // Update keepValues with hooks
  const keepValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_START,
      value: sharesStart,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesStart,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_END,
      value: sharesEnd,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesEnd,
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_START,
      value: valueStart,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueStart,
    },
    {
      label: StrategyReportThemedCardValuesLabels.VALUE_END,
      value: valueEnd,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setValueEnd,
    },
    {
      label: StrategyReportThemedCardValuesLabels.DIVIDENDS,
      value: dividends,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setDividends,
    },
    {
      label: StrategyReportThemedCardValuesLabels.UPSIDE_PARTICIPATION,
      value: upsideParticipation,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setUpsideParticipation,
    },
    {
      label: StrategyReportThemedCardValuesLabels.BASIS,
      value: basis,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setBasis,
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_SAVINGS,
      value: taxSavings,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setTaxSavings,
    },
  ];

  // Update protectValues with hooks
  const protectValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.MAX_STOCK_DROP,
      value: maxStockDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setMaxStockDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.OEA_DROP,
      value: oeaDrop,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOeaDrop,
    },
    {
      label: StrategyReportThemedCardValuesLabels.COLLAR_PL,
      value: collarPL,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setCollarPL,
    },
    {
      label: StrategyReportThemedCardValuesLabels.AVAILABLE_MARGIN_BALANCE,
      value: availableMarginBalance,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setAvailableMarginBalance,
    },
  ];

  // Update diversifyValues with hooks
  const diversifyValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.OVERALL_DIVERSIFIED_PERFORMANCE,
      value: overallDiversifiedPerformance,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setOverallDiversifiedPerformance,
    },
    {
      label: StrategyReportThemedCardValuesLabels.TAX_LOSS_HARVEST_SLEEVE,
      value: taxLossHarvestSleeve,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setTaxLossHarvestSleeve,
    },
    {
      label: StrategyReportThemedCardValuesLabels.BEGINNING_CONCENTRATION,
      value: beginningConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setBeginningConcentration,
    },
    {
      label: StrategyReportThemedCardValuesLabels.CURRENT_CONCENTRATION,
      value: currentConcentration,
      type: SingleValueCardType.PERCENTAGE,
      onValueChange: setCurrentConcentration,
    },
    {
      label: StrategyReportThemedCardValuesLabels.HOLDINGS,
      value: holdings,
      type: SingleValueCardType.NUMBER,
      onValueChange: setHoldings,
    },
  ];

  // Update harvestTaxBenefitsValues with hooks
  const harvestTaxBenefitsValuesWithHooks: ReportValueCardProps[] = [
    {
      label: StrategyReportThemedCardValuesLabels.REALIZED_LOSSES,
      value: realizedLosses,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setRealizedLosses,
    },
    {
      label: StrategyReportThemedCardValuesLabels.LOSSES_USED,
      value: lossesUsed,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setLossesUsed,
    },
    {
      label: StrategyReportThemedCardValuesLabels.POOL_REMAINING,
      value: poolRemaining,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setPoolRemaining,
    },
    {
      label: StrategyReportThemedCardValuesLabels.UNREALIZED_GAINS,
      value: unrealizedGains,
      type: SingleValueCardType.CURRENCY,
      onValueChange: setUnrealizedGains,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SHARES_SOLD,
      value: sharesSold,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSharesSold,
    },
    {
      label: StrategyReportThemedCardValuesLabels.SELLABLE_TODAY,
      value: sellableToday,
      type: SingleValueCardType.NUMBER,
      onValueChange: setSellableToday,
    },
  ];

  const [chartDataPoints, setChartDataPoints] = useState<
    ReportChartDataPoint[]
  >([]);
  const [drawdownChartLimit, setDrawdownChartLimit] = useStrategyReportValue(
    "drawdown_chart_limit_strategy",
    STRATEGY_REPORT_DEFAULTS.DRAWDOWN_CHART_LIMIT,
  );
  const [drawdownChartNegativeLimit, setDrawdownChartNegativeLimit] =
    useStrategyReportValue(
      "drawdown_chart_negative_limit_strategy",
      STRATEGY_REPORT_DEFAULTS.DRAWDOWN_CHART_NEGATIVE_LIMIT,
    );
  const [isTransactionModalOpen, setIsTransactionModalOpen] = useState(false);
  // The bucket report intentionally has no exclude_transactions yet (batch
  // contract kept minimal until the aggregation phase) — deselection is
  // collected here so the modal keeps working, but it doesn't affect the
  // calculation for now.
  const [deselectedTransactions, setDeselectedTransactions] = useState<
    OrionTransaction[]
  >([]);
  const [getReportPdf, { isLoading: isPdfLoading }] = useGetReportPdfMutation();
  const { settingsInfo } = useReportSettings();
  // Strategy reports run over the account's persisted Bucket 1/2 — no
  // Strategy selection involved. This phase produces the combined excel
  // (one sheet per pair) + per-pair statuses; the cards/charts keep their
  // stored values until bucket aggregation lands.
  const [getBucketReport, { isLoading: isCalculating }] =
    useGetBucketReportMutation();
  const [calculationError, setCalculationError] = useState("");
  const [pairFailures, setPairFailures] = useState<BucketPairReportStatus[]>(
    [],
  );
  const [excelUrl, setExcelUrl] = useState<string | null>(null);

  const themedCardsConfig = useMemo<ReportThemedCardConfig[]>(
    () => [
      {
        label: StrategyReportThemedCardLabels.KEEP,
        description: StrategyReportThemedCardDescriptions.KEEP,
        values: keepValuesWithHooks,
        addictional_component: null,
      },
      {
        label: StrategyReportThemedCardLabels.PROTECT,
        description: StrategyReportThemedCardDescriptions.PROTECT,
        values: protectValuesWithHooks,
        addictional_component: {
          type: PDFReportChartType.BAR,
          title: DRAWDOWN_CHART_TITLE,
          limit: drawdownChartLimit as number,
          negativeLimit: drawdownChartNegativeLimit as number,
          data: [
            {
              label: CHART_SERIES_STOCK,
              value:
                typeof maxStockDrop === "number"
                  ? maxStockDrop
                  : parseFloat(String(maxStockDrop)) || 0,
              type: PDFReportValueType.PERCENTAGE,
            },
            {
              label: CHART_SERIES_OEA,
              value:
                typeof oeaDrop === "number"
                  ? oeaDrop
                  : parseFloat(String(oeaDrop)) || 0,
              type: PDFReportValueType.PERCENTAGE,
            },
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.DIVERSIFY,
        description: StrategyReportThemedCardDescriptions.DIVERSIFY,
        values: diversifyValuesWithHooks,
        addictional_component: {
          type: PDFReportChartType.DONUT,
          title: ALLOCATION_CHART_TITLE,
          limit: FULL_ALLOCATION_PCT,
          data: [
            {
              label: CHART_SERIES_STOCK,
              value: currentConcentration as number,
              type: PDFReportValueType.PERCENTAGE,
            },
            {
              label: CHART_SERIES_STRATEGY,
              value: FULL_ALLOCATION_PCT - (currentConcentration as number),
              type: PDFReportValueType.PERCENTAGE,
            },
          ],
        },
      },
      {
        label: StrategyReportThemedCardLabels.HARVEST,
        description: StrategyReportThemedCardDescriptions.HARVEST,
        values: harvestTaxBenefitsValuesWithHooks,
        addictional_component: null,
      },
    ],
    [
      keepValuesWithHooks,
      protectValuesWithHooks,
      diversifyValuesWithHooks,
      harvestTaxBenefitsValuesWithHooks,
      drawdownChartLimit,
      drawdownChartNegativeLimit,
      maxStockDrop,
      oeaDrop,
      currentConcentration,
    ],
  );

  const buildPdfRequest = useCallback((): PDFReportRequest => {
    const chart_data: ReportChartDataPoint[] = chartDataPoints.map((p) => ({
      date: String(p.date),
      option_total: Number(p.option_total),
      stock_total: Number(p.stock_total),
    }));

    const client_info: PDFReportClientInfoItem[] = [
      {
        label: StrategyReportSingleValueCardLabels.STARTING_VALUE,
        value: String(startingValue),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.STARTING_VALUE,
      },
      {
        label: StrategyReportSingleValueCardLabels.ENDING_VALUE_PDF,
        value: String(endingValue),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.ENDING_VALUE,
      },
      {
        label: StrategyReportSingleValueCardLabels.NET_CHANGE,
        value: String(netChange),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.NET_CHANGE,
      },
      {
        label: StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_PDF,
        value: String(timeWeightedReturn),
        type: PDFReportValueType.PERCENTAGE,
        description:
          StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN,
      },
      {
        label: StrategyReportSingleValueCardLabels.INCOME_RECEIVED,
        value: String(incomeReceived),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED,
      },
      {
        label: StrategyReportSingleValueCardLabels.MARGIN_INTEREST,
        value: String(marginInterest),
        type: PDFReportValueType.CURRENCY,
        description: StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST,
      },
    ];

    const theme_info: PDFReportThemeInfoItem[] = themedCardsConfig.map(
      (card) => ({
        label: card.label,
        description: card.description,
        values: toPdfValues(card.values),
        addictional_component: card.addictional_component,
      }),
    );

    return {
      type: "strategy",
      settings_info: settingsInfo,
      chart_data,
      client_info,
      theme_info,
    };
  }, [
    chartDataPoints,
    themedCardsConfig,
    settingsInfo,
    startingValue,
    endingValue,
    netChange,
    timeWeightedReturn,
    incomeReceived,
    marginInterest,
  ]);

  const handleDownloadPdf = useCallback(async () => {
    try {
      const response: ReportPdfResponse =
        await getReportPdf(buildPdfRequest()).unwrap();
      if (response.download_url) {
        const a = document.createElement("a");
        a.href = response.download_url;
        a.click();
        a.remove();
        return;
      }
      throw new Error("Failed to download PDF");
    } catch (error) {
      throw new Error("Failed to download PDF");
    }
  }, [getReportPdf, buildPdfRequest]);

  const openTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(true);
  }, []);

  const closeTransactionModal = useCallback(() => {
    setIsTransactionModalOpen(false);
  }, []);

  const handleCalculate = useCallback(async () => {
    const [start_date, end_date] = settingsInfo.period;

    if (!settingsInfo.orion_account_id || !start_date || !end_date) {
      setCalculationError(
        "Cannot calculate report: missing account or date range.",
      );
      return;
    }

    setPairFailures([]);
    try {
      // Buckets are CP-only: the backend runs one combined call+put report
      // per pair, so no option_type is sent. Bucket 1/2 belong to the page's
      // account; the selected Bucket 3 is passed by ID (it may belong to a
      // different account of the client) and adds one account-level sheet
      // with dynamically calculated values.
      const response = await getBucketReport({
        orion_account_id: settingsInfo.orion_account_id,
        start_date,
        end_date,
        buckets: [BucketNumberEnum.BUCKET_1, BucketNumberEnum.BUCKET_2],
        ...(settingsInfo.bucket_3_id
          ? { bucket_3_id: settingsInfo.bucket_3_id }
          : {}),
      }).unwrap();
      setExcelUrl(response.excel_url);
      // Pairs without option history still succeed (portfolio-only sheet)
      // — only real errors pop up here.
      setPairFailures(
        response.reports.filter(
          (report) => report.status === BucketReportStatusEnum.ERROR,
        ),
      );

      // Value application from the old single-pair strategy report — kept
      // commented for the aggregation phase, when the bucket report returns
      // calculation data (cards/themed cards/graph) again:
      //
      // const cards = data.single_info_cards;
      // setStartingValue(cards.card_1);
      // setEndingValue(cards.card_2);
      // setNetChange(cards.card_3);
      // setTimeWeightedReturn(cards.card_4);
      // setIncomeReceived(cards.card_5);
      // setMarginInterest(cards.card_6);
      //
      // const themedCards = data.themed_cards as
      //   | NewReportStrategyThemedCards
      //   | undefined;
      // if (themedCards && "keep" in themedCards) {
      //   setSharesStart(themedCards.keep.shares_start ?? (sharesStart as number));
      //   setSharesEnd(themedCards.keep.shares_end ?? (sharesEnd as number));
      //   setValueStart(themedCards.keep.value_start ?? (valueStart as number));
      //   setValueEnd(themedCards.keep.value_end ?? (valueEnd as number));
      //   setDividends(themedCards.keep.dividends ?? (dividends as number));
      //   setUpsideParticipation(
      //     themedCards.keep.upside_participation ?? (upsideParticipation as number),
      //   );
      //   setBasis(themedCards.keep.basis ?? (basis as number));
      //   setTaxSavings(themedCards.keep.tax_savings ?? (taxSavings as number));
      //
      //   setMaxStockDrop(
      //     themedCards.protect.max_stock_drop ?? (maxStockDrop as number),
      //   );
      //   setOeaDrop(themedCards.protect.oea_drop ?? (oeaDrop as number));
      //   setCollarPL(themedCards.protect.collar_pl ?? (collarPL as number));
      //   setAvailableMarginBalance(
      //     themedCards.protect.available_margin_balance ??
      //       (availableMarginBalance as number),
      //   );
      //
      //   setOverallDiversifiedPerformance(
      //     themedCards.diversify.overall_diversified_performance ??
      //       (overallDiversifiedPerformance as number),
      //   );
      //   setTaxLossHarvestSleeve(
      //     themedCards.diversify.tax_loss_harvest_sleeve ??
      //       (taxLossHarvestSleeve as number),
      //   );
      //   setBeginningConcentration(
      //     themedCards.diversify.beginning_concentration ??
      //       (beginningConcentration as number),
      //   );
      //   setCurrentConcentration(
      //     themedCards.diversify.current_concentration ??
      //       (currentConcentration as number),
      //   );
      //   setHoldings(themedCards.diversify.holdings ?? (holdings as number));
      //
      //   setRealizedLosses(
      //     themedCards.harvest.realized_losses ?? (realizedLosses as number),
      //   );
      //   setLossesUsed(themedCards.harvest.losses_used ?? (lossesUsed as number));
      //   setPoolRemaining(
      //     themedCards.harvest.pool_remaining ?? (poolRemaining as number),
      //   );
      //   setUnrealizedGains(
      //     themedCards.harvest.unrealized_gains ?? (unrealizedGains as number),
      //   );
      //   setSharesSold(themedCards.harvest.shares_sold ?? (sharesSold as number));
      //   setSellableToday(
      //     themedCards.harvest.sellable_today ?? (sellableToday as number),
      //   );
      // }
      //
      // setChartDataPoints(data.graph_or_table_data || []);
    } catch (e) {
      setCalculationError(
        extractReportErrorMessage(
          e,
          "Failed to calculate strategy report. Please try again.",
        ),
      );
    }
  }, [
    settingsInfo.period,
    settingsInfo.orion_account_id,
    settingsInfo.bucket_3_id,
    getBucketReport,
    setCalculationError,
  ]);

  const handleDownloadExcel = useCallback(() => {
    if (!excelUrl) return;
    const a = document.createElement("a");
    a.href = excelUrl;
    a.click();
    a.remove();
  }, [excelUrl]);

  // Reset Excel URL when key report inputs change
  useEffect(() => {
    setExcelUrl(null);
    setPairFailures([]);
  }, [
    settingsInfo.orion_account_id,
    settingsInfo.period?.[0],
    settingsInfo.period?.[1],
  ]);

  return (
    <Card>
      <CardContent>
        <RequestNotification
          open={Boolean(calculationError)}
          title="Calculation failed"
          message={calculationError}
          onClose={() => setCalculationError("")}
        />
        <RequestNotification
          open={pairFailures.length > 0}
          severity="warning"
          title={`${pairFailures.length} bucket report(s) failed`}
          message={pairFailures
            .map(
              (failure) =>
                `B${failure.bucket}${
                  failure.symbol ? ` ${failure.symbol}` : ""
                }${
                  failure.option_type ? ` ${failure.option_type}` : ""
                }: ${failure.message}`,
            )
            .join(" · ")}
          onClose={() => setPairFailures([])}
        />
        <SettingsReport
          type={PerformanceReportTypeEnum.STRATEGY}
          header="Optic Equity Access Performance"
          subtitle="OPTIC ASSET MANAGEMENT"
        />
        <Grid container justifyContent="center" sx={{ mt: 1 }}>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Generating…" : "Download PDF"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadExcel}
              disabled={!excelUrl}
            >
              {excelUrl ? "Download Excel" : "Download Excel"}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCalculate}
              disabled={isPdfLoading || isCalculating}
            >
              {isCalculating ? "Calculating…" : `Calculate`}
            </Button>
          </Grid>
          <Grid item sx={{ mr: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={openTransactionModal}
              disabled={isPdfLoading}
            >
              {isPdfLoading ? "Updating…" : "Transactions"}
            </Button>
          </Grid>
        </Grid>
        <ChartsReport
          chartDataPoints={chartDataPoints}
          onChartDataChange={setChartDataPoints}
        />
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={startingValue as number}
              label={StrategyReportSingleValueCardLabels.STARTING_VALUE}
              description={
                StrategyReportSingleValueCardDescriptions.STARTING_VALUE
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setStartingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={endingValue as number}
              label={StrategyReportSingleValueCardLabels.ENDING_VALUE_CARD}
              description={StrategyReportSingleValueCardDescriptions.ENDING_VALUE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setEndingValue}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={netChange as number}
              label={StrategyReportSingleValueCardLabels.NET_CHANGE}
              description={StrategyReportSingleValueCardDescriptions.NET_CHANGE}
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setNetChange}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={timeWeightedReturn as number}
              label={StrategyReportSingleValueCardLabels.TIME_WEIGHTED_RETURN_CARD}
              description={
                StrategyReportSingleValueCardDescriptions.TIME_WEIGHTED_RETURN
              }
              type={SingleValueCardType.PERCENTAGE}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setTimeWeightedReturn}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={incomeReceived as number}
              label={StrategyReportSingleValueCardLabels.INCOME_RECEIVED}
              description={
                StrategyReportSingleValueCardDescriptions.INCOME_RECEIVED
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.PRIMARY}
              onValueChange={setIncomeReceived}
            />
          </Grid>
          <Grid item lg={4} xs={12} md={6}>
            <SingleValueCard
              value={marginInterest as number}
              label={StrategyReportSingleValueCardLabels.MARGIN_INTEREST}
              description={
                StrategyReportSingleValueCardDescriptions.MARGIN_INTEREST
              }
              type={SingleValueCardType.CURRENCY}
              color={SingleValueCardColor.ERROR}
              onValueChange={setMarginInterest}
            />
          </Grid>
        </Grid>
        <Grid container spacing={4} columns={{ lg: 12, md: 6, xs: 12 }}>
          {themedCardsConfig.map((card, index) => (
            <Grid item lg={6} xs={12} md={6} key={card.label}>
              <MultiValuesCard
                title={card.label}
                values={card.values}
                description={
                  index === 0
                    ? StrategyReportThemedCardBlurbs.KEEP
                    : index === 1
                      ? StrategyReportThemedCardBlurbs.PROTECT
                      : index === 2
                        ? StrategyReportThemedCardBlurbs.DIVERSIFY
                        : StrategyReportThemedCardBlurbs.HARVEST
                }
                additionalComponents={
                  index === 1 ? (
                    <DrawdownComparisonChart
                      values={protectValuesWithHooks}
                      limit={drawdownChartLimit as number}
                      negativeLimit={drawdownChartNegativeLimit as number}
                      onLimitChange={setDrawdownChartLimit}
                      onNegativeLimitChange={setDrawdownChartNegativeLimit}
                    />
                  ) : index === 2 ? (
                    <CurrentAllocationChartReport
                      value={currentConcentration as number}
                    />
                  ) : null
                }
              />
            </Grid>
          ))}
        </Grid>
        <TransactionModal
          open={isTransactionModalOpen}
          onClose={closeTransactionModal}
          settingsInfo={settingsInfo}
          reportType={PerformanceReportTypeEnum.STRATEGY}
          onDeselectedTransactionsChange={setDeselectedTransactions}
        />
      </CardContent>
    </Card>
  );
}

export const StrategyReport = () => (
  <ReportSettingsProvider>
    <StrategyReportContent />
  </ReportSettingsProvider>
);

export default StrategyReport;
